import { points } from './points.js';
import * as ringIndicator from './ring-indicator.js';
import { openElement, toggleElement, closeElement } from './utils.js';
import * as l10n from '/src/js/l10n.js';
import * as header from '../components/header.js'

let player;
let audioElement;
let playControls, loading;
let seekBackwardBtn, seekForwardBtn, playPauseBtn, playIcon, pauseIcon;
let playing = false;
let currentPoint = null;

function init() {
  l10n.init();

  header.init();

  // Get elements
  player = document.getElementById('player');
  playControls = document.getElementById('play-controls');
  loading = document.getElementById('audioguide-player-loading-text');
  seekBackwardBtn = document.getElementById('seekBackward');
  seekForwardBtn = document.getElementById('seekForward');
  playPauseBtn = document.getElementById('play-pause');
  playIcon = document.getElementById('play-icon');
  pauseIcon = document.getElementById('pause-icon');
  audioElement = document.querySelector('audio');

  // Add event listeners for transcription button
  const transcriptionButtonElement = document.getElementById(
    'transcription-btn-toggle',
  );
  const transcriptionPanel = document.getElementById('transcription-panel');
  transcriptionButtonElement.addEventListener('click', (event) => {
    event.preventDefault();
    toggleElement(transcriptionPanel);
  });

  // Add event listeners for player buttons
  seekBackwardBtn.addEventListener('click', () => {
    audioElement.currentTime -= 5;
    updateProgress();
  });

  seekForwardBtn.addEventListener('click', () => {
    audioElement.currentTime += 5;
    updateProgress();
  });

  playPauseBtn.addEventListener('click', () => {
    if (playing) {
      audioElement.pause();
    } else {
      audioElement.play();
    }
  });

  // Periodically update the progress indicator
  setInterval(() => {
    if (playing) updateProgress();
  }, 1000);

  // Handle play event
  audioElement.addEventListener('play', (event) => {
    playing = true;
    playIcon.classList.add('hidden');
    pauseIcon.classList.remove('hidden');
    updateProgress();
  });

  // Handle pause event
  audioElement.addEventListener('pause', (event) => {
    playing = false;
    playIcon.classList.remove('hidden');
    pauseIcon.classList.add('hidden');
    updateProgress();
  });

  // Reset play/pause buttons when audio ends
  audioElement.addEventListener('ended', () => {
    playing = false;
    audioElement.currentTime = 0;
    playIcon.classList.remove('hidden');
    pauseIcon.classList.add('hidden');
    updateProgress();
  });

  // Reset play/pause buttons when audio ends
  audioElement.addEventListener('stalled', () => {
    // TODO: Handle audio stalling ?
    console.error('Audio stalled');
  });

  // Handle language change
  window.addEventListener('languagechange', () => {
    if (currentPoint) playPoint(currentPoint, true);
  });

  // Get the map element and clear it
  const mapElement = document.getElementById('points');
  mapElement.innerHTML = '';

  // Place points on the map
  points.forEach((point) => {
    const pointElement = document.createElement('button');

    // NOTE: Any Tailwind class modification here should be reflected in the
    // placeholder code in index.html
    pointElement.className = `listening-point absolute flex size-[10vw] group open:z-10`;
    pointElement.style = `left: ${point.pos[0]}%; top: ${point.pos[1]}%`;
    pointElement.innerHTML = `
      <span class="pointer-events-none absolute left-0 top-0 inline-flex h-full w-full rounded-full bg-[rgb(194,166,98)] group-open:motion-safe:animate-ping"></span>
      <span class="relative inline-flex size-[10vw] items-center justify-center rounded-full border-[1vw] border-[rgb(194,166,98)] bg-white text-center text-[5vw] font-semibold shadow shadow-black/30 group-open:scale-125 group-open:border-[rgb(194,166,98)] group-open:bg-black group-open:text-white">${point.number}
    `;
    pointElement.setAttribute('aria-label', `Point d'écoute ${point.number}`);
    pointElement.setAttribute('data-point', point.number);
    mapElement.appendChild(pointElement);

    // Add event listeners for point buttons
    pointElement.addEventListener('click', async (event) => {
      event.preventDefault();
      playPoint(point);
    });
  });
}

function playPoint(point, force = false) {
  const pointElement = document.querySelector(`[data-point="${point.number}"]`);
  const titleElement = document.querySelector('#title');
  const transcriptionPanel = document.getElementById('transcription-panel');

  currentPoint = point;

  if (playing) {
    // Stop the audio if it's playing
    audioElement.pause();
    audioElement.currentTime = 0;
    currentPoint = null;
  }

  // Close the player if it's already open
  if (pointElement.hasAttribute('open') && !force) {
    closeElement(pointElement);
    player.classList.add('hidden');
  } else {
    // Close all other points
    const elements = document.querySelectorAll('.listening-point');
    elements.forEach((el) => {
      if (el !== pointElement) {
        closeElement(el);
      }
    });

    // Open the clicked point
    openElement(pointElement);

    // Close transcription panel if open
    closeElement(transcriptionPanel);

    // Show loading animation
    playControls.classList.add('hidden');
    loading.classList.remove('hidden');

    // Set the title to "Loading..."
    titleElement.innerHTML = l10n.getLocalizedString('audioguide-player-loading-text');

    // Fetch transcription title and text
    const transcriptionText = fetch(`./text/${l10n.getLang()}${point.number.toString().padStart(2, '0')}.txt`)
    .then((response) => response.text())
    .then((text) => {
      // Get elements
      const transcriptionElement = document.querySelector('#transcription-content');

      // Split title and transcription
      const lines = text.split('\n');
      const title = lines[0];
      let transcription = lines.slice(2).join('\n\n').trim();

      // Detect missing transcription
      if (transcription.length === 0) {
        transcription = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.';
      }

      // Insert title and transcription into player
      titleElement.innerHTML = title;
      transcriptionElement.innerHTML = transcription;
      transcriptionElement.scrollTop = 0;

      // show player
      player.classList.remove('hidden');
    })
    .catch((error) => {
      console.error('Error:', error);
      alert('Le chargement du point d\'écoute a échoué. Veuillez réessayer.');
      closeElement(pointElement);
    });

    // Replace the audio source
    audioElement.src = `./audio/${l10n.getLang()}${point.number.toString().padStart(2, '0')}.mp3`;

    audioElement.addEventListener('canplay', (event) => {
      playControls.classList.remove('hidden');
      loading.classList.add('hidden');

      // Audio should autoplay from <audio autoplay="true">
      // The play controls are shown to the user otherwise
    });
  }
}

function updateProgress() {
  const progress = audioElement.currentTime / audioElement.duration;
  ringIndicator.setProgress(progress);
  const progressText = document.getElementById('time');
  const timeLeft = audioElement.duration - audioElement.currentTime
  const minutes = Math.floor(timeLeft / 60);
  const seconds = Math.floor(timeLeft % 60);
  progressText.innerHTML = `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`;
}

init();
ringIndicator.init('progress-indicator');
